import React, { useState } from 'react';

import { formatMoney, toMoney } from '@betterment/core';
import { useMutation } from '@betterment/js-runtime';
import { Box, Icon } from '@betterment/samba-react';
import { Button, Flash, Flex, Heading, Label, RadioInput, Text } from '@betterment/style-closet-react';

import { trackClick } from '../../features/track';
import { GoalIcon } from '../../Shared/components-v2/goal-icon/GoalIcon';
import { urlBuilder } from '../../Shared/urlBuilder';
import { Account, Customer } from '../data';

interface SwitchSpendingAccountProps {
  customer: Customer;
  onClose: () => void;
}

interface SuccessProps {
  currentSpendingAccount: Account;
  selectedAccount: Account;
  onClose: () => void;
}

interface AccountOptionProps {
  account: Account;
}

const AccountOption: React.FC<React.PropsWithChildren<AccountOptionProps>> = ({ account }) => {
  return (
    <Flex>
      <GoalIcon name="card" />
      <Box
        css={{
          marginLeft: 'space.compat-5',
        }}
      >
        <Flex flexDirection="column">
          <Text fontSize="2" lineHeight="1" textAlign="left">
            {account.name}
          </Text>
          <Text color="bmtGrey50" fontSize="1" textAlign="left">
            {formatMoney(toMoney(account.availableBalanceCents))}
            {account.isSpendingAccount && <Text color="inherit"> (Current)</Text>}
          </Text>
        </Flex>
      </Box>
    </Flex>
  );
};

const Success: React.FC<React.PropsWithChildren<SuccessProps>> = ({
  currentSpendingAccount,
  selectedAccount,
  onClose,
}) => {
  const isCurrentAccountSelected = selectedAccount === currentSpendingAccount;
  return (
    <Box>
      <Flex flexDirection="column" alignItems="flex-start">
        <Box
          css={{
            marginBottom: 'space.200',
          }}
        >
          <Icon name="CheckmarkCircle" size="large" color="icon.success" />
        </Box>
        <Heading variant="secondary" textAlign="left" pb="3">
          {isCurrentAccountSelected
            ? `Spending account set to ${selectedAccount.name}`
            : 'Your spending account switch has been initiated'}
        </Heading>
        {isCurrentAccountSelected ? (
          <Text as="p" textAlign="left" mb="10">
            Your card is ready to go.
          </Text>
        ) : (
          <>
            <Text as="p" textAlign="left">
              Your spending account will update to <strong>{selectedAccount.name}</strong> within one hour. In the
              meantime, you can continue to use your Betterment Visa&reg; Debit Card with your other account.
            </Text>
            <Text as="p" textAlign="left" mb="9">
              Remember, you can always transfer funds instantly between your Checking accounts.
            </Text>
          </>
        )}
        <Button onClick={onClose} px="11">
          Got it
        </Button>
      </Flex>
    </Box>
  );
};

const SwitchSpendingAccount: React.FC<React.PropsWithChildren<SwitchSpendingAccountProps>> = ({
  customer,
  onClose,
}) => {
  const currentSpendingAccount = customer.accounts.find(account => account.id === customer.card.spendingAccountId);
  const [selectedAccount, setSelectedAccount] = useState(currentSpendingAccount);
  const [shouldShowSuccess, setShowSuccess] = useState(false);
  const [switchSpendingAccountRequest] = useMutation<{ accountId: number }>(
    urlBuilder.postSwitchSpendingAccount(customer.card.id)
  );
  const [hasErrorsPresent, setErrorsPresent] = useState(false);
  const submitSwitch = async () => {
    const { errors } = await switchSpendingAccountRequest({ accountId: selectedAccount!.id });
    if (errors) {
      setErrorsPresent(true);
    } else {
      setShowSuccess(true);
    }
  };

  if (shouldShowSuccess) {
    return (
      <Success currentSpendingAccount={currentSpendingAccount!} selectedAccount={selectedAccount!} onClose={onClose} />
    );
  }

  return (
    <Box>
      <Flex flexDirection="column">
        <Heading variant="secondary" textAlign="left">
          Select a spending account
        </Heading>
        <Text as="p" textAlign="left">
          Your spending account is the account that will be drawn from when you use your Betterment Visa&reg; Debit
          Card.
        </Text>
        <Box
          css={{
            marginBottom: 'space.compat-7',
          }}
        >
          <Flash variant={hasErrorsPresent ? 'error' : 'info'}>
            {hasErrorsPresent ? (
              <Text as="p" textAlign="left">
                Something went wrong. Your spending account was not updated - please try again.
              </Text>
            ) : (
              <Text as="p" textAlign="left">
                Your spending account will update within one hour. You will not be able to make another change until the
                update is complete.
              </Text>
            )}
          </Flash>
        </Box>
        {customer.accounts.map(account => (
          <Box
            key={account.id}
            css={{
              marginBlock: 'space.compat-2',
              paddingBlock: 'space.compat-2',
            }}
          >
            <Label
              data-testid={`account-${account.id}`}
              fontWeight="normal"
              htmlFor={`account-${account.id}`}
              id={`account-${account.id}-label`}
            >
              <Flex alignItems="center">
                <RadioInput
                  id={`account-${account.id}`}
                  aria-labelledby={`account-label account-${account.id}-label`}
                  name="accountId"
                  value={account.id}
                  defaultChecked={account.isSpendingAccount}
                  onChange={() => setSelectedAccount(account)}
                />
                <AccountOption account={account} />
              </Flex>
            </Label>
          </Box>
        ))}

        <Flex.Item width={1} maxWidth="240px" mr="6" mt="8">
          <Button
            px="9"
            onClick={() => {
              trackClick({ name: 'SwitchSpendingAccount' });
              void submitSwitch();
            }}
          >
            Set spending account
          </Button>
        </Flex.Item>
      </Flex>
    </Box>
  );
};

export default SwitchSpendingAccount;
